/* eslint-disable react-hooks/set-state-in-effect, react/no-unescaped-entities --
 * Phase J-UX (Feb 2026): full Journal redesign.
 * The page is now reading-first: a calm header, primary "New entry"
 * CTA, quick insights row, search + collapsible filters, then a
 * timeline-grouped list of entries. Export tools moved into a
 * collapsed "Export & Reports" panel below the timeline so they
 * don't compete with the content.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import { ChildScopeBadge } from "@/components/ChildScopeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  BookOpen,
  Paperclip,
  CalendarDays,
  X,
  MapPin,
} from "lucide-react";
import { ChildPicker, validateChildSelection } from "@/components/ChildPicker";
import { downloadFile } from "@/lib/downloadFile";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { MoodPicker } from "@/components/journal/MoodPicker";
import { VoiceDictateButton } from "@/components/journal/VoiceDictateButton";
import { MediatorPdfDownloadButton } from "@/components/mediator/MediatorPdfDownloadButton";
import { JournalInsights } from "@/components/journal/JournalInsights";
import { JournalSearchFilters } from "@/components/journal/JournalSearchFilters";
import { JournalExportPanel } from "@/components/journal/JournalExportPanel";
import { JournalEntryCard } from "@/components/journal/JournalEntryCard";
import { JournalReadDialog } from "@/components/journal/JournalReadDialog";
import { JournalEditDialog } from "@/components/journal/JournalEditDialog";
import { bucketEntries } from "@/components/journal/journalTimeline";
import { toast } from "sonner";

export default function Journal() {
  const { user, isMediator } = useAuth();
  const { selectedChildId, children: scopeChildren } = useChildScope();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    recordThirdPartyView({
      resource_type: "journal",
      resource_label: "Opened journal page",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter state lives entirely in the URL so deep-links and the
  // browser back button just work.
  const activeFilter = (searchParams.get("filter") || "all").toLowerCase();
  const eventId = searchParams.get("eventId") || "";
  const q = searchParams.get("q") || "";
  const moodFilterRaw = (searchParams.get("mood") || "").trim();
  const fromDate = searchParams.get("from") || "";
  const toDate = searchParams.get("to") || "";

  const [entries, setEntries] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [readEntry, setReadEntry] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);

  // New-entry form state.
  const [form, setForm] = useState({
    title: "",
    body: "",
    is_shared: false,
    mood: null,
    mood_tags: [],
    location: "",
  });
  const [prefillSource, setPrefillSource] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [childIds, setChildIds] = useState([]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (activeFilter && activeFilter !== "all") params.set("filter", activeFilter);
    if (eventId) params.set("event_id", eventId);
    if (selectedChildId) params.set("child_id", selectedChildId);
    if (moodFilterRaw) params.set("mood", moodFilterRaw);
    if (q) params.set("q", q);
    const qs = params.toString();
    const { data } = await api.get(`/journal${qs ? `?${qs}` : ""}`);
    setEntries(data);
  }, [activeFilter, eventId, selectedChildId, moodFilterRaw, q]);
  useEffect(() => {
    load();
  }, [load]);

  // Prefill the new-entry dialog when arrived via a "Make journal
  // note" button on the Calendar page.
  useEffect(() => {
    const prefill = location.state?.prefillFromEvent;
    if (!prefill) return;
    setForm({
      title: prefill.title || "",
      body: prefill.body || "",
      is_shared: false,
      mood: null,
      mood_tags: [],
      location: "",
    });
    setPrefillSource({
      eventId: prefill.sourceEventId || null,
      title: prefill.title || "",
    });
    setNewOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Date-range filter is applied client-side (the server endpoint
  // doesn't accept from/to yet — and the journal payload is small).
  const filtered = useMemo(() => {
    if (!fromDate && !toDate) return entries;
    return entries.filter((e) => {
      if (!e.created_at) return true;
      const d = e.created_at.slice(0, 10);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
  }, [entries, fromDate, toDate]);

  const childrenById = useMemo(() => {
    const map = {};
    (scopeChildren || []).forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [scopeChildren]);

  // ----- New-entry submit -----
  const submit = async (e) => {
    e.preventDefault();
    const validationErr = validateChildSelection({
      value: childIds,
      hasChildren: (scopeChildren || []).length > 0,
      selectedChildId,
    });
    if (validationErr) {
      toast.error(validationErr);
      return;
    }
    setBusy(true);
    try {
      const attachment_ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        attachment_ids.push(up.data.id);
      }
      await api.post("/journal", {
        ...form,
        mood: form.mood ?? null,
        mood_tags: form.mood_tags || [],
        child_ids: childIds,
        attachment_ids,
        location: form.location || "",
        ...(prefillSource?.eventId
          ? {
              source_event_id: prefillSource.eventId,
              source_event_title: (prefillSource.title || "").replace(
                /^Journal note about: /,
                "",
              ),
            }
          : {}),
      });
      toast.success("Entry saved");
      setNewOpen(false);
      setForm({
        title: "",
        body: "",
        is_shared: false,
        mood: null,
        mood_tags: [],
        location: "",
      });
      setChildIds(selectedChildId ? [selectedChildId] : []);
      setPrefillSource(null);
      setFiles([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save entry.");
    } finally {
      setBusy(false);
    }
  };

  const exportSingle = (entry) => {
    downloadFile(
      `${API}/journal/${entry.id}/export/pdf`,
      `journal-entry-${entry.id.slice(0, 8)}.pdf`,
      {
        label: "Generating entry PDF",
        successMessage: "PDF ready",
        // Iter 125.hybrid-exports — hybrid email fallback (see Expenses.jsx).
        asyncFallback: {
          endpoint: "/exports/journal/pdf/start",
          payload: { entry_id: entry.id },
        },
      },
    );
  };

  const toggleShare = async (entry) => {
    try {
      await api.patch(`/journal/${entry.id}/share`, { is_shared: !entry.is_shared });
      toast.success(!entry.is_shared ? "Shared with co-parent" : "Marked private");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update sharing.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteEntry) return;
    try {
      await api.delete(`/journal/${deleteEntry.id}`);
      toast.success("Entry deleted");
      setDeleteEntry(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete entry.");
    }
  };

  const clearEventFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("eventId");
    setSearchParams(next, { replace: false });
  };

  const eventLabel = useMemo(() => {
    if (!eventId) return "";
    const m = entries.find((e) => e.source_event_id === eventId);
    return m?.source_event_title || "";
  }, [entries, eventId]);

  const buckets = useMemo(() => bucketEntries(filtered), [filtered]);

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">
            Private &amp; shared timeline
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1 inline-flex items-center gap-3 flex-wrap">
            Journal
            <ChildScopeBadge testid="journal-child-scope-badge" />
          </h1>
          <p className="text-sm text-[#6F828A] mt-1 max-w-prose">
            A calm, factual record of observations, concerns and patterns.
            Timestamps are permanent. Use the timeline below to read, search
            and link entries to the rest of the platform.
          </p>
        </div>

        {/* Primary action — New Entry */}
        <Dialog
          open={newOpen}
          onOpenChange={(v) => {
            setNewOpen(v);
            if (v) {
              setChildIds(selectedChildId ? [selectedChildId] : []);
            }
            if (!v) {
              setPrefillSource(null);
              setForm({
                title: "",
                body: "",
                is_shared: false,
                mood: null,
                mood_tags: [],
                location: "",
              });
              setChildIds(selectedChildId ? [selectedChildId] : []);
              setFiles([]);
            }
          }}
        >
          <DialogTrigger asChild>
            {isMediator ? (
              <span />
            ) : (
              <Button
                className="bg-[#1F9FA4] hover:bg-[#178085] rounded-sm h-10 px-4"
                data-testid="add-journal-button"
              >
                <Plus className="w-4 h-4 mr-2" /> New entry
              </Button>
            )}
          </DialogTrigger>
          {isMediator ? (
            <MediatorPdfDownloadButton
              to="/app/court-bundle"
              label="Download read-only PDF"
              testid="journal-mediator-pdf"
              className="flex-shrink-0"
            />
          ) : null}

          <DialogContent
            preventOutsideClose
            className="bg-white border-[#D9E1E3] max-h-[90vh] overflow-y-auto overflow-x-hidden"
          >
            <DialogHeader>
              <DialogTitle className="font-heading">New journal entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 min-w-0">
              {prefillSource ? (
                <div
                  className="flex items-start gap-2 text-xs text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm px-3 py-2"
                  data-testid="journal-prefill-source"
                >
                  <CalendarDays
                    className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"
                    strokeWidth={1.6}
                  />
                  <span>
                    Started from calendar event:{" "}
                    <span className="font-medium">
                      {prefillSource.title.replace(/^Journal note about: /, "")}
                    </span>
                    <span className="block text-[#116B6E]/80 mt-0.5">
                      Defaults to private. Toggle "Share with co-parent" below
                      if you want them to see it.
                    </span>
                  </span>
                </div>
              ) : null}
              <div>
                <Label>Title</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  data-testid="journal-title-input"
                />
              </div>
              <div>
                <Label>Details</Label>
                <Textarea
                  rows={6}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  required
                  data-testid="journal-body-input"
                />
                <div
                  className="mt-2 flex items-center gap-3 flex-wrap"
                  data-testid="journal-voice-row"
                >
                  <VoiceDictateButton
                    onTranscript={(text) =>
                      setForm((f) => ({
                        ...f,
                        body:
                          (f.body ? f.body + (f.body.endsWith(" ") ? "" : " ") : "") +
                          text,
                      }))
                    }
                  />
                  <span className="text-[11px] text-[#6F828A]">
                    Your voice becomes text you can edit before saving. Audio
                    never leaves your device.
                  </span>
                </div>
              </div>
              <MoodPicker
                value={form.mood}
                onChange={(v) => setForm({ ...form, mood: v })}
                tags={form.mood_tags}
                onTagsChange={(t) => setForm({ ...form, mood_tags: t })}
              />
              <ChildPicker
                value={childIds}
                onChange={setChildIds}
                testPrefix="journal-child-picker"
                label="Which child is this entry about?"
                hint="The entry will only appear under the selected child(ren)'s filter."
              />
              <div>
                <Label className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" strokeWidth={1.7} /> Location
                  (optional)
                </Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. School pickup, Contact centre, Home"
                  maxLength={200}
                  data-testid="journal-location-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-3.5 h-3.5" /> Attachments (optional)
                </Label>
                <Input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  className="w-full block max-w-full text-xs sm:text-sm"
                  data-testid="journal-attachments-input"
                />
                {files.length > 0 ? (
                  <div className="text-xs text-[#6F828A] mt-1">
                    {files.length} file(s) selected
                  </div>
                ) : null}
              </div>
              <div className="flex items-center justify-between border border-[#D9E1E3] rounded-sm p-3">
                <div>
                  <div className="text-sm font-medium">Share with co-parent</div>
                  <div className="text-xs text-[#6F828A]">
                    Off keeps this entry private to you.
                  </div>
                </div>
                <Switch
                  checked={form.is_shared}
                  onCheckedChange={(v) => setForm({ ...form, is_shared: v })}
                  data-testid="journal-share-switch"
                />
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="submit"
                  disabled={busy}
                  className="bg-[#1F9FA4] hover:bg-[#178085] w-full sm:w-auto h-11 sm:h-10"
                  data-testid="journal-save-button"
                >
                  {busy ? "Saving…" : "Save entry"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick insights */}
      <JournalInsights entries={filtered} userId={user?.id} />

      {/* Export & Reports — collapsed by default, sits ABOVE the
          filters as a page-level utility. Stays out of the way of
          the reading flow while remaining one click away. */}
      <JournalExportPanel
        selectedChildId={selectedChildId}
        activeFilter={activeFilter}
        eventId={eventId}
      />

      {/* Search + filters */}
      <JournalSearchFilters
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        resultCount={filtered.length}
      />

      {eventId ? (
        <div
          className="flex items-center gap-2 text-xs text-[#116B6E] bg-[#E0F2F2] border border-[#A7D8DA] rounded-sm px-3 py-2"
          data-testid="journal-event-filter-chip"
        >
          <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.6} />
          <span>
            Filtered to entries linked to{" "}
            <span className="font-medium">
              {eventLabel || "the selected calendar event"}
            </span>
          </span>
          <button
            type="button"
            onClick={clearEventFilter}
            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 hover:bg-[#D1ECEC] rounded-sm transition-colors"
            data-testid="journal-event-filter-clear"
            aria-label="Clear event filter"
          >
            <X className="w-3 h-3" />
            Clear
          </button>
        </div>
      ) : null}

      {/* Timeline */}
      {filtered.length === 0 ? (
        <div
          className="border border-[#D9E1E3] bg-white rounded-md p-10 text-center"
          data-testid="journal-empty-state"
        >
          <BookOpen className="w-8 h-8 mx-auto text-[#6F828A]" strokeWidth={1.4} />
          <p className="mt-3 text-[#6F828A]">
            {q ||
            activeFilter !== "all" ||
            eventId ||
            fromDate ||
            toDate ||
            moodFilterRaw
              ? "No entries match this view. Try clearing a filter or search term."
              : "No entries yet. Use \"New entry\" above to record an observation."}
          </p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="journal-timeline">
          {buckets.map((bucket) => (
            <section
              key={bucket.key}
              className="space-y-3"
              data-testid={`journal-bucket-${bucket.key}`}
            >
              <h2
                className="text-[11px] uppercase tracking-[0.14em] font-medium text-[#6F828A] sticky top-0 bg-[#F4F8F9] py-1"
                data-testid={`journal-bucket-label-${bucket.key}`}
              >
                {bucket.label}
                <span className="ml-2 text-[#A0B3BA] font-mono-data lowercase tracking-normal">
                  · {bucket.entries.length}
                </span>
              </h2>
              <div className="space-y-3">
                {bucket.entries.map((e) => (
                  <JournalEntryCard
                    key={e.id}
                    entry={e}
                    childrenById={childrenById}
                    currentUserId={user?.id}
                    onOpen={(en) => setReadEntry(en)}
                    onEdit={(en) => setEditEntry(en)}
                    onShareToggle={(en) => toggleShare(en)}
                    onDelete={(en) => setDeleteEntry(en)}
                    onExportPdf={(en) => exportSingle(en)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Read / Edit / Delete dialogs */}
      <JournalReadDialog
        open={!!readEntry}
        onOpenChange={(v) => !v && setReadEntry(null)}
        entry={readEntry}
        childrenById={childrenById}
      />
      <JournalEditDialog
        open={!!editEntry}
        onOpenChange={(v) => !v && setEditEntry(null)}
        entry={editEntry}
        onSaved={() => {
          setEditEntry(null);
          load();
        }}
      />
      <AlertDialog
        open={!!deleteEntry}
        onOpenChange={(v) => !v && setDeleteEntry(null)}
      >
        <AlertDialogContent
          className="bg-white border-[#D9E1E3]"
          data-testid="journal-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Delete this journal entry?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#3F535A]">
              This will remove the entry from your timeline. The action is
              logged in the audit trail. You can&apos;t undo it from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="journal-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-[#7E3B25] hover:bg-[#65301F]"
              data-testid="journal-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
